/**
 * Delete Project Dialog Component
 * @implements US-017 - Project Deletion
 * @implements BS-002.10 - Project Deletion (30-day cooling-off)
 * @implements TS-070 - Delete Project UI
 *
 * Multi-step dialog for project deletion:
 * 1. Warning step - explains consequences
 * 2. Re-authentication step - verifies user identity
 * 3. Deletion step - executes the delete mutation
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import {
  DeleteForever as DeleteForeverIcon,
  Block as BlockIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useMutation, gql } from '@apollo/client';
import {
  ReauthenticationDialog,
  getReauthCookie,
  clearReauthCookie,
  getPendingReauthAction,
  clearPendingReauthAction,
} from '@/components/auth';

/**
 * GraphQL mutation for deleting a project
 * @implements TS-070 - Delete Project UI
 */
const DELETE_PROJECT_MUTATION = gql`
  mutation DeleteProject($projectId: ID!) {
    deleteProject(projectId: $projectId) {
      project {
        id
        lifecycleState
        deletionRequestedAt
        scheduledDestructionDate
      }
      patch {
        op
        path
        value
      }
    }
  }
`;

/**
 * Error code mapping for user-friendly error messages
 * @implements TS-070 - Delete Project UI
 */
const ERROR_MESSAGES: Record<string, string> = {
  INVALID_LIFECYCLE_STATE_FOR_DELETION:
    'This project cannot be deleted in its current state.',
  PROJECT_NOT_IN_NORMAL_STATE:
    'Project must be in Normal control state to delete. Please complete or discard any pending changes first.',
  PROJECT_NOT_FOUND: 'Project not found.',
  UNAUTHORIZED: 'You do not have permission to delete this project.',
  UNAUTHENTICATED: 'Please re-authenticate to continue.',
  FORBIDDEN: 'You do not have permission to perform this action.',
};

/**
 * Extract user-friendly error message from GraphQL error
 */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Check for known error codes in the message
    for (const [code, message] of Object.entries(ERROR_MESSAGES)) {
      if (error.message.includes(code)) {
        return message;
      }
    }
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Dialog step type
 */
type DialogStep = 'warning' | 'reauth' | 'deleting';

/**
 * Props for DeleteProjectDialog component
 */
export interface DeleteProjectDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** The project ID to delete */
  projectId: string;
  /** The project title (for display) */
  projectTitle: string;
  /** Callback when the dialog is closed (cancel) */
  onClose: () => void;
  /** Callback when the project is successfully deleted */
  onDeleted: () => void;
}

/**
 * Delete Project Dialog Component
 * @implements TS-070 - Delete Project UI
 *
 * Three-step dialog flow:
 * 1. Warning: Shows deletion consequences with clear messaging
 * 2. Re-authentication: Requires user to verify identity
 * 3. Deletion: Executes the delete mutation
 *
 * Features:
 * - Clear warning about 30-day cooling-off period
 * - Explains immediate inaccessibility
 * - Re-authentication requirement before proceeding
 * - Loading state during deletion
 * - Error handling with user-friendly messages
 *
 * @example
 * ```tsx
 * <DeleteProjectDialog
 *   open={dialogOpen}
 *   projectId={project.id}
 *   projectTitle={project.title}
 *   onClose={() => setDialogOpen(false)}
 *   onDeleted={() => {
 *     navigate('/projects');
 *     showSuccess('Project scheduled for deletion');
 *   }}
 * />
 * ```
 */
export const DeleteProjectDialog = ({
  open,
  projectId,
  projectTitle,
  onClose,
  onDeleted,
}: DeleteProjectDialogProps) => {
  const [step, setStep] = useState<DialogStep>('warning');
  const [error, setError] = useState<string | null>(null);

  // Delete project mutation
  const [deleteProject, { loading: isDeleting }] = useMutation(
    DELETE_PROJECT_MUTATION,
    {
      variables: { projectId },
      onCompleted: () => {
        setError(null);
        onDeleted();
      },
      onError: (err) => {
        const message = getErrorMessage(err);
        setError(message);
        // Go back to warning step on error so user can try again
        setStep('warning');
      },
      // Update Apollo cache with the response
      update: (cache, { data }) => {
        if (data?.deleteProject?.project) {
          cache.modify({
            id: cache.identify({ __typename: 'Project', id: projectId }),
            fields: {
              lifecycleState: () => data.deleteProject.project.lifecycleState,
              deletionRequestedAt: () =>
                data.deleteProject.project.deletionRequestedAt,
              scheduledDestructionDate: () =>
                data.deleteProject.project.scheduledDestructionDate,
            },
          });
        }
      },
    },
  );

  /**
   * Check for OAuth re-auth callback on mount
   * @implements TS-070 - Delete Project UI (OAuth re-auth flow)
   *
   * When returning from OAuth re-auth, check if:
   * 1. The reauth_verified cookie exists
   * 2. There's a pending delete action for this project
   * If both conditions are met, auto-proceed with deletion.
   */
  useEffect(() => {
    if (!open) return;

    const reauthCookie = getReauthCookie();
    const pendingAction = getPendingReauthAction();

    // Check if this is a valid OAuth re-auth callback for this project
    if (
      reauthCookie &&
      pendingAction &&
      pendingAction.action === 'delete_project' &&
      pendingAction.projectId === projectId
    ) {
      // Clear the cookie and localStorage
      clearReauthCookie();
      clearPendingReauthAction();

      // Auto-proceed with deletion (skip warning and reauth steps)
      setStep('deleting');
      deleteProject();
    }
  }, [open, projectId, deleteProject]);

  /**
   * Handle dialog close - reset state
   */
  const handleClose = useCallback(() => {
    if (!isDeleting) {
      setStep('warning');
      setError(null);
      onClose();
    }
  }, [isDeleting, onClose]);

  /**
   * Handle proceed from warning step to re-authentication
   */
  const handleProceedToReauth = useCallback(() => {
    setError(null);
    setStep('reauth');
  }, []);

  /**
   * Handle successful re-authentication - proceed to delete
   */
  const handleReauthSuccess = useCallback(() => {
    setStep('deleting');
    deleteProject();
  }, [deleteProject]);

  /**
   * Handle cancel from re-authentication - go back to warning
   */
  const handleReauthCancel = useCallback(() => {
    setStep('warning');
  }, []);

  // Show re-authentication dialog when on that step
  if (step === 'reauth') {
    return (
      <ReauthenticationDialog
        open={open}
        onClose={handleReauthCancel}
        onSuccess={handleReauthSuccess}
        title="Confirm Your Identity"
        description={`To delete "${projectTitle}", please verify your identity.`}
        pendingAction="delete_project"
        projectId={projectId}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={isDeleting ? undefined : handleClose}
      aria-labelledby="delete-project-dialog-title"
      aria-describedby="delete-project-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        id="delete-project-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'error.main',
        }}
      >
        {step === 'deleting' ? (
          <CircularProgress size={24} color="error" />
        ) : (
          <WarningIcon color="error" />
        )}
        {step === 'deleting' ? 'Deleting Project...' : 'Delete Project?'}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 'warning' && (
          <>
            <DialogContentText
              id="delete-project-dialog-description"
              component="div"
            >
              Are you sure you want to delete <strong>{projectTitle}</strong>?
              This is a serious action with the following consequences:
              <List dense sx={{ mt: 2 }}>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <BlockIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Immediate inaccessibility"
                    secondary="The project will become inaccessible to you and all team members immediately"
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ScheduleIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="30-day cooling-off period"
                    secondary="The project will be permanently deleted after 30 days"
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <DeleteForeverIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Cannot be undone after 30 days"
                    secondary="After the cooling-off period, all project data will be permanently destroyed"
                  />
                </ListItem>
              </List>
            </DialogContentText>
            <Alert severity="warning" sx={{ mt: 2 }}>
              You will need to re-authenticate before proceeding.
            </Alert>
          </>
        )}

        {step === 'deleting' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <DialogContentText>
              Please wait while we process your request...
            </DialogContentText>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 'warning' && (
          <>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleProceedToReauth}
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
            >
              Delete Project
            </Button>
          </>
        )}
        {step === 'deleting' && (
          <Button disabled color="inherit">
            Please wait...
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
